import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createChart,
  ColorType,
  CrosshairMode,
  CandlestickSeries,
  HistogramSeries,
  LineStyle,
} from 'lightweight-charts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faSync } from '@fortawesome/free-solid-svg-icons';
import { cryptoApi } from '../api/cryptoApi';
import { useTheme } from '../contexts/ThemeContext';
import { readDeskColors, withAlpha } from '../utils/deskColors';

const TIMEFRAMES = [
  { value: '1Min', label: '1m', hours: 12 },
  { value: '5Min', label: '5m', hours: 48 },
  { value: '15Min', label: '15m', hours: 7 * 24 },
  { value: '1Hour', label: '1H', hours: 30 * 24 },
  { value: '4Hour', label: '4H', hours: 90 * 24 },
  { value: '1Day', label: '1D', hours: 365 * 24 },
];

function isIntraday(tf) {
  return /Min|Hour/i.test(String(tf));
}

function formatBars(rawBars, timeframe) {
  const intraday = isIntraday(timeframe);
  return (rawBars || [])
    .map((bar) => {
      const utcDate = bar.timestamp != null ? new Date(bar.timestamp) : bar.t ? new Date(bar.t) : null;
      if (!utcDate || Number.isNaN(utcDate.getTime())) return null;
      let time;
      if (intraday) {
        time = Math.floor(utcDate.getTime() / 1000);
      } else {
        const y = utcDate.getUTCFullYear();
        const m = String(utcDate.getUTCMonth() + 1).padStart(2, '0');
        const d = String(utcDate.getUTCDate()).padStart(2, '0');
        time = `${y}-${m}-${d}`;
      }
      const open = Number(bar.open ?? bar.o);
      const high = Number(bar.high ?? bar.h);
      const low = Number(bar.low ?? bar.l);
      const close = Number(bar.close ?? bar.c);
      const volume = Number(bar.volume ?? bar.v ?? 0);
      if (![open, high, low, close].every(Number.isFinite)) return null;
      return { time, open, high, low, close, volume };
    })
    .filter(Boolean)
    .sort((a, b) => (typeof a.time === 'string' ? a.time.localeCompare(b.time) : a.time - b.time))
    .filter((bar, i, arr) => i === 0 || bar.time !== arr[i - 1].time);
}

function priceDigits(n) {
  const abs = Math.abs(Number(n) || 0);
  if (abs >= 1000) return 2;
  if (abs >= 1) return 4;
  if (abs >= 0.01) return 6;
  return 8;
}

function formatPrice(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  const d = priceDigits(v);
  return v.toLocaleString(undefined, { minimumFractionDigits: Math.min(2, d), maximumFractionDigits: d });
}

function formatVolume(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return v.toFixed(v >= 1 ? 2 : 4);
}

const CryptoBarsChart = ({ symbol, height = 360 }) => {
  const { theme } = useTheme();
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const priceSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const barsRef = useRef([]);

  const [timeframe, setTimeframe] = useState('1Hour');
  const [bars, setBars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hud, setHud] = useState(null);

  const preset = useMemo(() => TIMEFRAMES.find((t) => t.value === timeframe) || TIMEFRAMES[3], [timeframe]);

  const loadBars = useCallback(async () => {
    if (!symbol) return;
    try {
      setLoading(true);
      setError(null);
      const end = new Date();
      const start = new Date(end.getTime() - preset.hours * 3600 * 1000);
      const response = await cryptoApi.getBars(symbol, {
        timeframe,
        limit: 1500,
        start: start.toISOString(),
        end: end.toISOString(),
      });
      const next = formatBars(response.data || [], timeframe);
      setBars(next);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load bars');
      setBars([]);
    } finally {
      setLoading(false);
    }
  }, [symbol, timeframe, preset.hours]);

  useEffect(() => {
    loadBars();
  }, [loadBars]);

  useEffect(() => {
    if (!chartContainerRef.current) return undefined;

    const colors = readDeskColors();
    const chart = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: colors.muted,
        fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
        fontSize: 11,
      },
      grid: {
        vertLines: { color: colors.grid },
        horzLines: { color: colors.grid },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: withAlpha(colors.action, 0.45), width: 1, style: LineStyle.Dashed, labelBackgroundColor: colors.hover },
        horzLine: { color: withAlpha(colors.action, 0.45), width: 1, style: LineStyle.Dashed, labelBackgroundColor: colors.hover },
      },
      rightPriceScale: {
        borderColor: 'rgba(255,255,255,0.08)',
        scaleMargins: { top: 0.08, bottom: 0.22 },
      },
      timeScale: {
        borderColor: 'rgba(255,255,255,0.08)',
        rightOffset: 6,
        barSpacing: 6,
        minBarSpacing: 2,
        timeVisible: true,
        secondsVisible: false,
      },
      width: chartContainerRef.current.clientWidth,
      height,
    });

    const priceSeries = chart.addSeries(CandlestickSeries, {
      upColor: colors.candleUp,
      downColor: colors.candleDown,
      borderVisible: false,
      wickUpColor: colors.candleUp,
      wickDownColor: colors.candleDown,
    });
    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: 'volume',
      lastValueVisible: false,
      priceLineVisible: false,
    });
    chart.priceScale('volume').applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });

    chartRef.current = chart;
    priceSeriesRef.current = priceSeries;
    volumeSeriesRef.current = volumeSeries;

    chart.subscribeCrosshairMove((param) => {
      if (!param?.time || !param.seriesData?.size) {
        const latest = barsRef.current[barsRef.current.length - 1];
        if (latest) setHud({ ...latest, live: true });
        return;
      }
      const candle = param.seriesData.get(priceSeries);
      const matched = barsRef.current.find((b) => b.time === param.time);
      if (candle?.open != null) {
        setHud({
          open: candle.open,
          high: candle.high,
          low: candle.low,
          close: candle.close,
          volume: matched?.volume,
          live: false,
        });
      }
    });

    const onResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({ width: chartContainerRef.current.clientWidth, height });
      }
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      chart.remove();
      chartRef.current = null;
    };
  }, [height, symbol, theme]);

  useEffect(() => {
    barsRef.current = bars;
    const latest = bars[bars.length - 1];
    if (latest) setHud({ ...latest, live: true });
    if (!priceSeriesRef.current || !volumeSeriesRef.current) return;
    priceSeriesRef.current.setData(bars.map(({ time, open, high, low, close }) => ({ time, open, high, low, close })));
    const colors = readDeskColors();
    volumeSeriesRef.current.setData(
      bars.map((b) => ({
        time: b.time,
        value: b.volume,
        color: b.close >= b.open ? withAlpha(colors.candleUp, 0.45) : withAlpha(colors.candleDown, 0.45),
      }))
    );
    chartRef.current?.timeScale().fitContent();
  }, [bars, theme]);

  const up = hud && hud.close >= hud.open;

  return (
    <div className="relative h-full">
      <div className="flex items-center justify-between gap-3 px-3 pt-2 pb-1">
        <div className="flex items-center gap-2 text-[11px] num min-h-[22px]">
          {hud ? (
            <>
              <span className="text-muted">O</span>
              <span className={up ? 'text-bull' : 'text-bear'}>{formatPrice(hud.open)}</span>
              <span className="text-muted">H</span>
              <span>{formatPrice(hud.high)}</span>
              <span className="text-muted">L</span>
              <span>{formatPrice(hud.low)}</span>
              <span className="text-muted">C</span>
              <span className={up ? 'text-bull' : 'text-bear'}>{formatPrice(hud.close)}</span>
              <span className="text-muted">Vol</span>
              <span>{formatVolume(hud.volume)}</span>
              {hud.live && <span className="text-muted text-[10px]">live</span>}
            </>
          ) : (
            <span className="text-muted">{loading ? 'Loading…' : 'No bars'}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf.value}
              type="button"
              onClick={() => setTimeframe(tf.value)}
              className={`px-2 py-0.5 text-[10px] uppercase tracking-wide rounded ${
                timeframe === tf.value ? 'bg-action text-white' : 'text-muted hover:bg-elevated hover:text-ink'
              }`}
            >
              {tf.label}
            </button>
          ))}
          <button type="button" onClick={loadBars} className="ml-1 px-2 py-0.5 text-muted hover:text-ink" title="Refresh">
            <FontAwesomeIcon icon={faSync} className={`text-[10px] ${loading ? 'fa-spin' : ''}`} />
          </button>
        </div>
      </div>
      {error && <div className="px-3 pb-1 text-[11px] text-bear">{error}</div>}
      <div ref={chartContainerRef} className="w-full" style={{ height }} />
      {loading && bars.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <FontAwesomeIcon icon={faSpinner} spin className="text-muted" />
        </div>
      )}
    </div>
  );
};

export default CryptoBarsChart;
